import type {
  CashierPaymentMethod,
  CashierTradingAccount,
  SavedPaymentAccountSummary,
} from "@/api/cashier";
import { ErrorPage } from "@/components/error-page/ErrorPage";
import { SelectableCard } from "@/components/selectable-card/SelectableCard";
import { Tooltip } from "@/components/tooltip/Tooltip";
import { withOfflineTooltip } from "@/components/tooltip/withOfflineTooltip";
import { useCurrencies } from "@/hooks/deposit/useCurrencies";
import { useFormatAmount } from "@/hooks/useFormatAmount";
import { SearchContext } from "@/pages/withdrawals/choose-method/payment-method-selector-card/components/SearchContext";
import { SelectMenu } from "@/pages/withdrawals/choose-method/payment-method-selector-card/components/SelectMenu";
import type {
  ItemType,
  SelectMenuItemType,
} from "@/pages/withdrawals/choose-method/payment-method-selector-card/types";
import { usePaymentMethodSelectorItemMappers } from "@/pages/withdrawals/choose-method/payment-method-selector-card/useMappers";
import { EIcon } from "@/utils/enums";
import { getAssetUrl } from "@/utils/helpers";
import { Button, Icon, Tag } from "@pepperstone/pepper-ui-kit";
import clsx from "clsx";
import type React from "react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Select, { type ActionMeta, type SingleValue } from "react-select";
import { isPaymentMethodOffline } from "@/utils/paymentMethodStatus";
import styles from "./PaymentMethodSelectorCard.module.css";

const OfflineTooltip = withOfflineTooltip(Tooltip);

type PaymentMethodSelectorCardProps = {
  tradingAccount: CashierTradingAccount;
  savedPaymentAccounts: SavedPaymentAccountSummary[];
  paymentMethods: CashierPaymentMethod[];
  selectedPaymentAccount: SavedPaymentAccountSummary | undefined;
  selectedNewPaymentMethod: CashierPaymentMethod | undefined;
  onChange: (
    savedAccount: SavedPaymentAccountSummary | undefined,
    newProfitMethod: CashierPaymentMethod | undefined
  ) => void;
  selected?: boolean;
  disabled?: boolean;
  children?: React.ReactNode;
};

export const PaymentMethodSelectorCard = ({
  tradingAccount,
  savedPaymentAccounts,
  selectedPaymentAccount,
  onChange,
  paymentMethods,
  selectedNewPaymentMethod,
  selected,
  children,
  disabled,
}: PaymentMethodSelectorCardProps) => {
  const { t } = useTranslation();
  const { formatFee } = useFormatAmount();
  const { currencies } = useCurrencies();
  const {
    convertSavedPaymentAccountSummaryToOptionItem,
    convertNewPaymentMethodToOptionItem,
  } = usePaymentMethodSelectorItemMappers(tradingAccount.currency);

  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const closeMenu = useCallback(() => {
    setMenuIsOpen(false);
  }, []);
  const toggleMenu = useCallback(() => {
    setMenuIsOpen(!menuIsOpen);
  }, [menuIsOpen]);

  const [searchValue, setSearchValue] = useState<string>("");
  const [listMode, setListMode] = useState<ItemType>("saved");

  const onSelectChange = useCallback(
    (
      option: SingleValue<SelectMenuItemType>,
      action: ActionMeta<SelectMenuItemType>
    ) => {
      if (action.action === "select-option") {
        if (!option) {
          onChange(undefined, undefined);
        } else {
          if (option.type === "saved") {
            onChange(option.data, undefined);
          }
          if (option.type === "new") {
            onChange(undefined, option.data);
          }
        }
        closeMenu();
      }
    },
    [onChange, closeMenu]
  );

  const onListModeChange = useCallback(
    (listMode: ItemType) => {
      setListMode(listMode);
      onChange(undefined, undefined);
    },
    [onChange]
  );

  const options = useMemo(() => {
    if (listMode === "saved") {
      return savedPaymentAccounts.map(
        convertSavedPaymentAccountSummaryToOptionItem
      );
    }
    return paymentMethods.map((item) =>
      convertNewPaymentMethodToOptionItem(item)
    );
  }, [
    listMode,
    savedPaymentAccounts,
    paymentMethods,
    convertSavedPaymentAccountSummaryToOptionItem,
    convertNewPaymentMethodToOptionItem,
  ]);

  const selectedData = useMemo(() => {
    let data = {
      id: "",
      title: t(
        "withdrawals.methods.payment_method_selector_card.payment_method"
      ),
      detail: "--",
      logo_url: getAssetUrl("default.svg", "icons/methods"),
      type: "saved",
      data: {},
    } as SelectMenuItemType;

    if (listMode === "saved" && selectedPaymentAccount) {
      data = convertSavedPaymentAccountSummaryToOptionItem(
        selectedPaymentAccount
      );
    }

    if (listMode === "new" && selectedNewPaymentMethod) {
      data = convertNewPaymentMethodToOptionItem(selectedNewPaymentMethod);
    }

    return data;
  }, [
    listMode,
    selectedPaymentAccount,
    selectedNewPaymentMethod,
    t,
    convertNewPaymentMethodToOptionItem,
    convertSavedPaymentAccountSummaryToOptionItem,
    tradingAccount.currency,
  ]);

  const selectedCard = useMemo(() => {
    const isExpiryLike = (value: string) => /^\d{2}\/\d{2}$/.test(value.trim());
    const warning = (text: string, key: string) => (
      <span key={key} className={styles.selectedWarningSubText}>
        {text}
      </span>
    );

    if (listMode === "saved" && selectedPaymentAccount) {
      const matchedPaymentMethod = paymentMethods.find(
        (pm) => pm.id === selectedPaymentAccount.payment_method_id
      );

      const payoutInfoMessage =
        matchedPaymentMethod && "payout" in matchedPaymentMethod.base_settings
          ? matchedPaymentMethod.base_settings.payout.info_message
          : "";

      const expiryText = selectedPaymentAccount.secondary_detail
        ? isExpiryLike(selectedPaymentAccount.secondary_detail)
          ? t("deposit.methods.saved_methods.expiration", {
              expiryDate: selectedPaymentAccount.secondary_detail,
            })
          : selectedPaymentAccount.secondary_detail
        : "";

      const subText = [
        selectedPaymentAccount.detail,
        expiryText,
        payoutInfoMessage
          ? warning(
              payoutInfoMessage,
              `saved-warning-${selectedPaymentAccount.id ?? "no-id"}`
            )
          : null,
      ].filter(Boolean);

      return {
        title: selectedPaymentAccount.name,
        subText,
        icon: selectedPaymentAccount.logo_url,
      };
    }

    if (listMode === "new" && selectedNewPaymentMethod) {
      const processingTime =
        "deposit" in selectedNewPaymentMethod.base_settings
          ? selectedNewPaymentMethod.base_settings.deposit
              .expected_processing_time
          : selectedNewPaymentMethod.base_settings.payout
              .expected_processing_time;

      const comment =
        "deposit" in selectedNewPaymentMethod.base_settings
          ? selectedNewPaymentMethod.base_settings.deposit.info_message
          : selectedNewPaymentMethod.base_settings.payout.info_message;

      return {
        title: selectedNewPaymentMethod.display_name,
        subText: [
          processingTime || "",
          comment
            ? warning(comment, `new-warning-${selectedNewPaymentMethod.id}`)
            : null,
        ].filter(Boolean),
        icon: selectedNewPaymentMethod.logo_url,
      };
    }

    return {
      title: selectedData.title,
      subText: selectedData.detail,
      icon: selectedData.logo_url,
    };
  }, [
    listMode,
    paymentMethods,
    selectedData.detail,
    selectedData.logo_url,
    selectedData.title,
    selectedNewPaymentMethod,
    selectedPaymentAccount,
  ]);

  const selectedItem =
    listMode === "saved" ? selectedPaymentAccount : selectedNewPaymentMethod;
  const selectedOffline = isPaymentMethodOffline(selectedItem);

  return (
    <SearchContext.Provider
      value={{
        listMode,
        searchValue,
        setListMode: onListModeChange,
        setSearchValue,
      }}
    >
      <OfflineTooltip
        offline={selectedOffline}
        disabledReason={selectedItem?.disabled_reason}
      >
        <SelectableCard
          data-testid={"withdrawal-methods-selector-card"}
          disabled={disabled || selectedOffline}
          selected={selected}
          className={clsx(styles.card, selected && styles.cardSelected)}
          id={selectedData.id}
          title={selectedCard.title}
          subText={selectedCard.subText}
          icon={selectedCard.icon}
          iconPadding={selectedData.id ? "xxs" : undefined}
          hideCheckbox
          hideChildSeparatorLine
          button={
            <Button
              data-testid={"withdrawal-methods-selector-card-button"}
              type="button"
              variant="ps-btn-transparent"
              icon="arrow-right"
              iconPosition="end"
              size="ps-btn-sm"
              onClick={toggleMenu}
              disabled={disabled || selectedOffline}
            >
              <span className={styles.selectButtonText}>
                {t("withdrawals.methods.payment_method_selector_card.select")}
              </span>
            </Button>
          }
        >
          <Select
            data-testid={"withdrawal-methods-selector-select"}
            unstyled
            components={{
              Control: () => null,
              Menu: SelectMenu,
            }}
            classNames={{
              option: () => styles.selectOption,
              menu: () => styles.selectContainer,
            }}
            options={options}
            value={selectedData}
            getOptionValue={(option) => option.id}
            getOptionLabel={(option) => option.title}
            isOptionDisabled={(item: SelectMenuItemType) =>
              isPaymentMethodOffline(item.data)
            }
            formatOptionLabel={(item: SelectMenuItemType) => {
              const offline = isPaymentMethodOffline(item.data);
              const selected =
                item.type === "saved"
                  ? selectedPaymentAccount?.id === item.id
                  : selectedNewPaymentMethod?.id === item.id;
              const rawFee =
                item.type === "saved"
                  ? item.data.fee
                  : item.data.settings[tradingAccount.currency.code]?.fee;

              let feeLabel: string | null = null;
              if (rawFee === null) {
                feeLabel = t("withdrawals.fee.no_fee");
              } else if (rawFee?.type === "percentage") {
                feeLabel = t("withdrawals.fee.percentage", {
                  value: rawFee.value.amount,
                });
              } else if (rawFee?.type === "fixed") {
                const currency = currencies?.[rawFee.value.code];
                feeLabel = t("withdrawals.fee.fixed", {
                  value: formatFee(rawFee, currency),
                });
              }

              return (
                <OfflineTooltip
                  offline={offline}
                  disabledReason={item.data.disabled_reason}
                  usePortal
                >
                  <SelectableCard
                    className={clsx(
                      styles.selectOptionCard,
                      selected && styles.selected
                    )}
                    disabled={offline}
                    id={item.id}
                    icon={item.logo_url}
                    iconSize="sm"
                    iconPadding="xxs"
                    title={
                      <div className="ps-flex ps-gap-2">
                        {item.title}
                        {feeLabel && (
                          <Tag bgColor="foreground-neutral" label={feeLabel} />
                        )}
                      </div>
                    }
                    subText={
                      item.type === "new" ? (
                        <span className={styles.dropdownSubText}>
                          {Array.isArray(item.detail)
                            ? item.detail.join(" ")
                            : item.detail}
                        </span>
                      ) : item.type === "saved" && item.detail ? (
                        <span className={styles.dropdownSubText}>
                          {Array.isArray(item.detail)
                            ? item.detail.join(" ")
                            : item.detail}
                        </span>
                      ) : (
                        ""
                      )
                    }
                    hideCheckbox
                    button={
                      selected ? (
                        <Icon icon="check-mark" color="primary" />
                      ) : null
                    }
                  />
                </OfflineTooltip>
              );
            }}
            menuIsOpen={menuIsOpen}
            isSearchable={true}
            inputValue={searchValue}
            onChange={onSelectChange}
            noOptionsMessage={({ inputValue }) => (
              <ErrorPage
                className={styles.emptyState}
                icon={EIcon.EmptyResultError}
                title={t(
                  "withdrawals.methods.payment_method_selector_card.no_records"
                )}
                message={
                  inputValue
                    ? ""
                    : t(
                        "withdrawals.methods.payment_method_selector_card.no_profit_methods"
                      )
                }
                showButton={false}
              />
            )}
          />
          {children}
        </SelectableCard>
      </OfflineTooltip>
    </SearchContext.Provider>
  );
};
